import { create } from "zustand";
import { AxiosError } from "axios";
import { apiClient } from "@/api/client";
import useAuthStore from "@/store/authStore";

interface UserData {
  first_name: string;
  last_name: string;
  password: string;
  password_confirmation: string;
  number: string;
  country_code: string;
  address: string;
  date_of_birth: string;
  language: string;
  timezone: string;
  bio: string;
  token: string;
}

interface Address {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  country: string;
  zip_postal: string;
}

interface MediaRequestData {
  name: string;
  website: string;
  phone: string;
  industry: string;
  size: string;
  main_email: string;
  logo: File | null;
  shipping: Address;
  billing: Address;
}

interface OnboardingState {
  userData: Partial<UserData>;
  sendUserData: (data: Partial<UserData>) => Promise<void>;
  createMediaAgency: (agencyData: MediaRequestData) => Promise<void>;
  validateToken: (token: string) => Promise<boolean>;
}

const useOnboardingStore = create<OnboardingState>((set) => ({
  userData: {},

  sendUserData: async (data) => {
    try {
      const response = await apiClient.post(
        `/global-media-agency-invitations/accept/${data.token}`,
        data
      );
      useAuthStore
        .getState()
        .setAuth(
          response.data.user,
          response.data.token,
          null,
          null,
          null,
          null
        );
      set({ userData: response.data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  createMediaAgency: async (agencyData: MediaRequestData) => {
    try {
      const formData = new FormData();

      formData.append("name", agencyData.name);
      formData.append("website", agencyData.website);
      formData.append("phone", agencyData.phone);
      formData.append("industry", agencyData.industry);
      formData.append("size", agencyData.size);
      formData.append("main_email", agencyData.main_email);

      formData.append("logo", agencyData.logo);

      Object.entries(agencyData.shipping).forEach(([key, value]) => {
        formData.append(`shipping[${key}]`, value);
      });

      Object.entries(agencyData.billing).forEach(([key, value]) => {
        formData.append(`billing[${key}]`, value);
      });

      //TODO: Upload the logo

      const response = await apiClient.post(
        "/media-agency-information",
        formData,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("authToken"),
            "Content-Type": "mutipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  validateToken: async (token) => {
    try {
      const res = await apiClient.get(
        `/global-media-agency-invitations/${token}`
      );
      const tokenResponse = res.data.data;
      return tokenResponse;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      if (axiosError.status == 500) {
        const errorMessage = axiosError.response
          ? axiosError.response.data?.message || "Something went wrong"
          : "Something went wrong";
        throw new Error(errorMessage);
      }
      return false;
    }
  },
}));

export default useOnboardingStore;
